import type { Module, ReturnType } from "@clientflow/shared";

export type Client = {
  id: string;
  kind: "individual" | "business";
  displayName: string;
  legalName: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  status: "prospect" | "active" | "inactive" | "archived";
  assignedUserId: string | null;
  notes: string | null;
  clientCode: string | null;
  modules?: Module[];
  /** Dashboards the firm keeps out of this client's portal. */
  reviewOptOut?: boolean;
  portalHiddenModules?: string[];
};

export type Stage = { key: string; name: string; clientLabel: string };

export type Template = {
  id: string;
  module: Module;
  name: string;
  stages: Stage[];
  returnType?: ReturnType | null;
  defaultTasks: Array<{ title: string; stageKey?: string; dueInDays?: number }>;
  defaultRequests: Array<{ type: string; title: string; dueInDays?: number }>;
};

export type Engagement = {
  id: string;
  kind?: "work" | "planning";
  clientId: string;
  clientName?: string;
  templateId: string | null;
  module: Module;
  name: string;
  period: string | null;
  stageKey: string;
  assignedUserId: string | null;
  dueDate: string | null;
  status: "open" | "completed" | "cancelled";
  createdAt: string;
  returnType?: ReturnType | null;
  yearEndMonth?: number | null;
  reviewerUserId?: string | null;
  reviewSignedOffAt?: string | null;
  reviewSignedOffByUserId?: string | null;
  quotedFeeCents?: number | null;
};

export type Task = {
  id: string;
  title: string;
  clientId: string | null;
  clientName?: string | null;
  engagementId: string | null;
  dueDate: string | null;
  status: "open" | "done" | "cancelled";
  overdue?: boolean;
};

export type ClientRequest = {
  id: string;
  clientId: string;
  clientName?: string;
  engagementId: string | null;
  type: "document" | "question" | "approval";
  title: string;
  instructions: string | null;
  status: "open" | "submitted" | "accepted" | "cancelled";
  dueDate: string | null;
  remindersSent: number;
  responseText: string | null;
  createdAt: string;
};

export type Doc = {
  id: string;
  clientId: string;
  folder: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  visibility?: "shared" | "internal";
  createdAt: string;
  requestId: string | null;
  /** The year the document is filed under. */
  year: number;
  /** Staff filed it under a different year than its default. */
  movedToYear?: boolean;
  lockedByInvoiceId?: string | null;
  /** Portal only: held until an invoice is paid. */
  locked?: boolean;
};

export type Member = {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  staffModules: string[];
  /** Services whose dashboard is hidden from this person (they still see the work). */
  hiddenDashboards?: string[];
  isActive: boolean;
  pending?: boolean;
  mfaEnabled: boolean;
};

/** Where each service lives in the menu: its home page and its workflow (engagement list) page. */
export const SERVICE_PATHS: Record<Module, { home: string; workflow: string; workflowLabel: string }> = {
  tax: { home: "/tax", workflow: "/tax/returns", workflowLabel: "Tax Returns" },
  bookkeeping: { home: "/accounting", workflow: "/accounting/workflow", workflowLabel: "Close Workflow" },
  payroll: { home: "/payroll", workflow: "/payroll/workflow", workflowLabel: "Payroll Workflow" },
  advisory: { home: "/advisory", workflow: "/advisory/workflow", workflowLabel: "Advisory Workflow" },
};
